/// <reference lib="dom" />

export type LandingVariation = 'A' | 'B';
export const LANDING_VARIATION_KEY = 'landing_page_variation';
/** Storage key for the short-vs-long test; a new key re-randomizes visitors from the old hero test. */
export const VSL_TEST_KEY = 'vsl_test_v2';

/**
 * Self-contained so the static renderer can inject it at the very top of <head>.
 * /vsl is split 50/50 and redirected (before anything renders) to its variant's own URL;
 * /vsl-a and /vsl-b are pinned. Returns the variant, or undefined off the VSL pages.
 */
export function initializeVslExperiment(): 'A' | 'B' | undefined {
  const url = new URL(window.location.href);
  const path = url.pathname.replace(/\/+$/, '').toLowerCase();
  if (!['/vsl', '/vsl-a', '/vsl-b'].includes(path)) return;
  const key = 'landing_page_variation';
  const storeKey = 'vsl_test_v2';
  const explicit = path === '/vsl-a' ? 'A' : path === '/vsl-b' ? 'B' : url.searchParams.get(key);
  let saved: string | null = null;
  try { saved = localStorage.getItem(storeKey); } catch { /* unavailable */ }
  if (saved !== 'A' && saved !== 'B') {
    try { saved = sessionStorage.getItem(storeKey); } catch { /* unavailable */ }
  }
  const variant = explicit === 'A' || explicit === 'B' ? explicit
    : saved === 'A' || saved === 'B' ? saved
    : crypto.getRandomValues(new Uint32Array(1))[0] < 0x80000000 ? 'A' : 'B';
  try { sessionStorage.setItem(storeKey, variant); } catch { /* unavailable */ }
  try { localStorage.setItem(storeKey, variant); } catch { /* unavailable */ }
  document.documentElement.dataset.vslLandingVariation = variant;
  url.searchParams.set(key, variant);
  const target = (path === '/vsl' ? (variant === 'A' ? '/vsl-a' : '/vsl-b') : url.pathname) + url.search + url.hash;
  if (path === '/vsl' && typeof document !== 'undefined' && document.readyState === 'loading' && !document.body) {
    // Still parsing <head>: a real redirect so the visitor lands on the variant's own page before any paint.
    window.location.replace(target);
  } else {
    window.history.replaceState(window.history.state, '', target);
  }
  return variant;
}

export function withLandingVariation(base: string, variation?: string): string {
  if (variation !== 'A' && variation !== 'B') return base;
  const url = new URL(base, window.location.origin);
  if (!['http:', 'https:'].includes(url.protocol)) return base;
  url.searchParams.set(LANDING_VARIATION_KEY, variation);
  return base.startsWith('/') ? url.pathname + url.search + url.hash : url.href;
}
